import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";
import {
  BellRing,
  CalendarClock,
  CheckCircle2,
  Copy,
  KeyRound,
  CreditCard,
  FlaskConical,
  Link as LinkIcon,
  Loader2,
  Mail,
  Save,
  Send,
  Sheet as SheetIcon,
  Trash2,
  Unplug,
  XCircle,
} from "lucide-react";

const ConfiguredPill = ({ ok, okLabel, badLabel, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-medium ${
      ok
        ? "border-[hsl(var(--status-verified))]/40 bg-[hsl(var(--status-verified))]/10 text-[hsl(var(--status-verified))]"
        : "border-border bg-secondary text-muted-foreground"
    }`}
  >
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    {ok ? okLabel : badLabel}
  </span>
);

export const AdminIntegrationsTab = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    googleSheetId: "",
    googleSheetTab: "Orders",
    googleServiceAccountJson: "",
    googleClientId: "",
    googleClientSecret: "",
    adminNotifyEmail: "",
    senderEmail: "",
    telegramBotToken: "",
    telegramChatId: "",
  });
  const [saving, setSaving] = useState(false);
  const [testingSheets, setTestingSheets] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
      setForm((prev) => ({
        ...prev,
        googleSheetId: data.googleSheetId || "",
        googleSheetTab: data.googleSheetTab || "Orders",
        googleClientId: data.googleClientId || "",
        adminNotifyEmail: data.adminNotifyEmail || "",
        senderEmail: data.senderEmail || "",
        telegramChatId: data.telegramChatId || "",
      }));
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load integration settings."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const save = async (keys) => {
    setSaving(true);
    try {
      const payload = {};
      keys.forEach((key) => {
        if (key === "googleServiceAccountJson" && !form.googleServiceAccountJson.trim()) return;
        if (key === "telegramBotToken" && !form.telegramBotToken.trim()) return;
        if (key === "googleClientSecret" && !form.googleClientSecret.trim()) return;
        payload[key] = form[key];
      });
      const { data } = await api.put("/admin/settings", payload);
      setSettings(data);
      setForm((prev) => ({ ...prev, googleServiceAccountJson: "", telegramBotToken: "", googleClientSecret: "" }));
      toast.success("Settings saved.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Saving failed."));
    } finally {
      setSaving(false);
    }
  };

  const testSheets = async () => {
    setTestingSheets(true);
    try {
      const { data } = await api.post("/admin/settings/test-sheets");
      toast.success(data.message || "Google Sheets connection works.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Google Sheets test failed."), { duration: 9000 });
    } finally {
      setTestingSheets(false);
    }
  };

  const testTelegram = async () => {
    setTestingTelegram(true);
    try {
      await api.post("/telegram/test");
      toast.success("Telegram test message sent. Check your chat.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Telegram test failed."));
    } finally {
      setTestingTelegram(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(data.message || "Weekly digest sent.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Digest could not be sent."));
    } finally {
      setSendingDigest(false);
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await api.post("/admin/reminders/send");
      toast.success(data.message || "Reminders processed.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Reminders could not be sent."));
    } finally {
      setSendingReminders(false);
    }
  };

  const [google, setGoogle] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [testingGoogle, setTestingGoogle] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [savingToken, setSavingToken] = useState(false);
  const [resetOptions, setResetOptions] = useState({ tickets: true, orders: true, members: false });
  const [resetting, setResetting] = useState(false);
  const [creatingTestMember, setCreatingTestMember] = useState(false);
  const [inviteCodes, setInviteCodes] = useState(null);
  const [generatingCodes, setGeneratingCodes] = useState(false);

  const loadInviteCodes = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/invite-codes");
      setInviteCodes(data.codes);
    } catch (error) {
      setInviteCodes([]);
    }
  }, []);

  useEffect(() => {
    loadInviteCodes();
  }, [loadInviteCodes]);

  const generateCodes = async () => {
    setGeneratingCodes(true);
    try {
      await api.post("/admin/invite-codes", { count: 5 });
      toast.success("5 new beta invite codes generated.");
      loadInviteCodes();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not generate codes."));
    } finally {
      setGeneratingCodes(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch (error) {
      toast.error("Could not copy — select the code manually.");
    }
  };

  const createTestMember = async () => {
    setCreatingTestMember(true);
    try {
      const { data } = await api.post("/admin/test-member", {});
      toast.success(data.message || "Test member created.", { duration: 12000 });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not create the test member."));
    } finally {
      setCreatingTestMember(false);
    }
  };

  const runReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-test-data", resetOptions);
      toast.success(data.message || "Test data cleared.");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Reset failed."), { duration: 9000 });
    } finally {
      setResetting(false);
    }
  };

  const loadGoogle = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/google/status");
      setGoogle(data);
    } catch (error) {
      // Non-critical status poll; log so failures are still visible in devtools.
      console.warn("Google status refresh failed:", error?.message || error);
    }
  }, []);

  useEffect(() => {
    loadGoogle();
  }, [loadGoogle]);

  const connectGoogle = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/admin/google/connect");
      window.location.href = data.authorizationUrl;
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not start the Google connection."));
      setConnecting(false);
    }
  };

  const testGoogle = async () => {
    setTestingGoogle(true);
    try {
      const { data } = await api.post("/admin/google/test");
      toast.success(data.message || "Google Drive & Sheets are working.");
      loadGoogle();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Google integration test failed."), { duration: 9000 });
    } finally {
      setTestingGoogle(false);
    }
  };

  const disconnectGoogle = async () => {
    setDisconnecting(true);
    try {
      await api.post("/admin/google/disconnect");
      toast.success("Google disconnected.");
      loadGoogle();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Disconnect failed."));
    } finally {
      setDisconnecting(false);
    }
  };

  const connectWithToken = async () => {
    if (!manualToken.trim()) {
      toast.error("Paste the refresh token from the Google OAuth Playground first.");
      return;
    }
    setSavingToken(true);
    try {
      const { data } = await api.post("/admin/google/manual-token", { refreshToken: manualToken.trim() });
      toast.success(data.message || "Google connected via refresh token.");
      setManualToken("");
      loadGoogle();
    } catch (error) {
      toast.error(apiErrorMessage(error, "The refresh token was rejected."), { duration: 12000 });
      loadGoogle();
    } finally {
      setSavingToken(false);
    }
  };

  if (!settings) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <div className="space-y-6">
        {/* Stripe payments */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <CreditCard className="h-4.5 w-4.5 h-5 w-5 text-primary" /> Stripe payments
              </CardTitle>
              <ConfiguredPill
                ok={settings.stripeConfigured}
                okLabel={settings.stripeLiveMode ? "Live payments active" : "Test mode"}
                badLabel="Not configured"
                testid="stripe-configured-pill"
              />
            </div>
            <CardDescription>
              Members pay through Stripe Checkout using your account's membership prices. Payments are verified
              server-side the moment the member returns — no webhook setup required.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground" data-testid="stripe-mode-note">
              {settings.demoMode
                ? "Demo mode is ON — checkouts are simulated and no cards are charged."
                : settings.stripeLiveMode
                ? "LIVE mode — real cards are charged. Monthly tiers bill as subscriptions; annual tiers are one-time yearly payments."
                : "Stripe key detected. Add your live key to charge real cards."}
            </p>
            <p className="text-xs text-muted-foreground">
              Optional hardening: add a webhook in your Stripe dashboard pointing to
              <span className="font-mono"> /api/stripe/webhook</span> and set its signing secret in the backend
              environment. This is a safety net only — payments already complete without it.
            </p>
          </CardContent>
        </Card>

        {/* Google Drive & Sheets */}
        <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <SheetIcon className="h-5 w-5 text-primary" /> Google Drive &amp; Sheets
            </CardTitle>
            <ConfiguredPill
              ok={Boolean(google?.connected)}
              okLabel={google?.accountEmail ? `Connected: ${google.accountEmail}` : "Connected"}
              badLabel="Not connected"
              testid="google-connected-pill"
            />
          </div>
          <CardDescription>
            One click connects your Gmail Drive. Every member gets their own folder (documents, receipts,
            reports) plus an orders sheet, and every order event exports to a master sheet — with an email
            to you as it happens.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {!google?.connected ? (
              <Button
                size="sm"
                data-testid="google-connect-button"
                disabled={connecting || !google?.oauthConfigured}
                onClick={connectGoogle}
              >
                {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <LinkIcon className="h-4 w-4 mr-1.5" />}
                Connect Google
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="google-test-button"
                  disabled={testingGoogle}
                  onClick={testGoogle}
                >
                  {testingGoogle ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FlaskConical className="h-4 w-4 mr-1.5" />}
                  Run full test
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  data-testid="google-disconnect-button"
                  disabled={disconnecting}
                  onClick={disconnectGoogle}
                >
                  {disconnecting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Unplug className="h-4 w-4 mr-1.5" />}
                  Disconnect
                </Button>
              </>
            )}
          </div>
          {google?.lastError && (
            <div
              className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 space-y-1"
              data-testid="google-last-error"
            >
              <p className="text-xs font-semibold text-destructive">
                Last connection attempt failed — {google.lastError.error}
                {google.lastError.stage ? ` (during ${google.lastError.stage})` : ""}
              </p>
              {google.lastError.description && (
                <p className="text-xs text-destructive/90" data-testid="google-last-error-description">
                  Google said: {google.lastError.description}
                </p>
              )}
              {google.lastError.redirect_uri && (
                <p className="text-[11px] text-muted-foreground font-mono break-all">
                  Redirect URI used: {google.lastError.redirect_uri}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                {google.lastError.at ? `At ${new Date(google.lastError.at).toLocaleString()} · ` : ""}
                server build {google.lastError.build || "unknown"}
              </p>
            </div>
          )}
          {google?.connected && (google.rootFolderLink || google.masterSheetLink) && (
            <div className="space-y-1 text-xs" data-testid="google-links">
              {google.rootFolderLink && (
                <p>
                  <a className="text-primary underline underline-offset-2" href={google.rootFolderLink} target="_blank" rel="noreferrer" data-testid="google-root-folder-link">
                    Open the GetYourQSIP Portal folder in Drive
                  </a>
                </p>
              )}
              {google.masterSheetLink && (
                <p>
                  <a className="text-primary underline underline-offset-2" href={google.masterSheetLink} target="_blank" rel="noreferrer" data-testid="google-master-sheet-link">
                    Open the Master Orders sheet
                  </a>
                </p>
              )}
            </div>
          )}
          {!google?.connected && (
            <div className="space-y-4" data-testid="google-setup-section">
              <div className="rounded-lg border border-border bg-secondary/40 p-3.5 space-y-3">
                <p className="text-xs font-semibold text-foreground">
                  One-time setup with your getyourqsip@gmail.com account (about 5 minutes):
                </p>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal pl-4 leading-relaxed">
                  <li>
                    Signed in as <span className="font-mono">getyourqsip@gmail.com</span>, open{" "}
                    <a className="text-primary underline underline-offset-2" href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noreferrer">
                      console.cloud.google.com/projectcreate
                    </a>{" "}
                    and create a project (name it anything, e.g. GetYourQSIP).
                  </li>
                  <li>
                    Enable both APIs:{" "}
                    <a className="text-primary underline underline-offset-2" href="https://console.cloud.google.com/apis/library/drive.googleapis.com" target="_blank" rel="noreferrer">
                      Google Drive API
                    </a>{" "}
                    and{" "}
                    <a className="text-primary underline underline-offset-2" href="https://console.cloud.google.com/apis/library/sheets.googleapis.com" target="_blank" rel="noreferrer">
                      Google Sheets API
                    </a>{" "}
                    (click Enable on each).
                  </li>
                  <li>
                    Open{" "}
                    <a className="text-primary underline underline-offset-2" href="https://console.cloud.google.com/auth/overview" target="_blank" rel="noreferrer">
                      Google Auth Platform
                    </a>{" "}
                    → configure the consent screen: user type <strong className="text-foreground">External</strong>, then on the
                    Audience page click <strong className="text-foreground">Publish app</strong> (prevents weekly disconnects).
                  </li>
                  <li>
                    Go to{" "}
                    <a className="text-primary underline underline-offset-2" href="https://console.cloud.google.com/apis/credentials/oauthclient" target="_blank" rel="noreferrer">
                      Credentials → Create OAuth client ID
                    </a>
                    : type <strong className="text-foreground">Web application</strong>, and under Authorized redirect URIs paste
                    the URL below exactly.
                  </li>
                  <li>Copy the Client ID and Client Secret Google shows you into the fields below, save, then click Connect Google.</li>
                </ol>
                <div className="space-y-1.5">
                  <Label className="text-xs">Authorized redirect URI (paste into Google exactly)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={google?.redirectUri || ""}
                      className="font-mono text-[11px]"
                      data-testid="google-redirect-uri-field"
                      onFocus={(event) => event.target.select()}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="google-redirect-uri-copy"
                      onClick={() => {
                        navigator.clipboard?.writeText(google?.redirectUri || "");
                        toast.success("Redirect URI copied.");
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="google-client-id" className="text-xs">Client ID</Label>
                    <Input
                      id="google-client-id"
                      value={form.googleClientId}
                      onChange={setField("googleClientId")}
                      placeholder="xxxxxxxx.apps.googleusercontent.com"
                      className="font-mono text-[11px]"
                      data-testid="google-client-id-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="google-client-secret" className="text-xs">
                      Client Secret {settings.googleClientSecretMasked ? `(saved: ${settings.googleClientSecretMasked})` : ""}
                    </Label>
                    <Input
                      id="google-client-secret"
                      type="password"
                      value={form.googleClientSecret}
                      onChange={setField("googleClientSecret")}
                      placeholder={settings.googleClientSecretMasked ? "Leave blank to keep saved secret" : "GOCSPX-..."}
                      className="font-mono text-[11px]"
                      data-testid="google-client-secret-input"
                    />
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  data-testid="google-save-credentials-button"
                  disabled={saving}
                  onClick={async () => {
                    await save(["googleClientId", "googleClientSecret"]);
                    loadGoogle();
                  }}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
                  Save Google credentials
                </Button>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="google-connect-hint">
                Then click Connect Google and sign in as getyourqsip@gmail.com, approving BOTH the Drive and
                Sheets permissions. If Google shows "hasn't been verified", click Advanced → Continue — it is
                your own app.
              </p>
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3.5 space-y-3" data-testid="google-manual-token-section">
                <p className="text-xs font-semibold text-foreground">
                  Plan B — connect WITHOUT the redirect (works even if Connect Google keeps failing):
                </p>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal pl-4 leading-relaxed">
                  <li>
                    In your Google OAuth client (Credentials page), add a second Authorized redirect URI:{" "}
                    <span className="font-mono text-foreground">https://developers.google.com/oauthplayground</span>
                  </li>
                  <li>
                    Open{" "}
                    <a className="text-primary underline underline-offset-2" href="https://developers.google.com/oauthplayground" target="_blank" rel="noreferrer">
                      Google OAuth Playground
                    </a>
                    , click the gear icon (top right), tick <strong className="text-foreground">Use your own OAuth credentials</strong>,
                    and paste the same Client ID and Client Secret you saved above.
                  </li>
                  <li>
                    In Step 1, paste both scopes into the input box (space-separated) and click Authorize APIs:{" "}
                    <span className="font-mono text-[10px] break-all text-foreground">
                      https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/spreadsheets
                    </span>
                  </li>
                  <li>Sign in as <span className="font-mono">getyourqsip@gmail.com</span> and approve.</li>
                  <li>
                    In Step 2, click <strong className="text-foreground">Exchange authorization code for tokens</strong>,
                    copy the <strong className="text-foreground">Refresh token</strong>, and paste it below.
                  </li>
                </ol>
                <div className="flex items-center gap-2">
                  <Input
                    value={manualToken}
                    onChange={(event) => setManualToken(event.target.value)}
                    placeholder="1//0abc... (refresh token from the Playground)"
                    className="font-mono text-[11px]"
                    data-testid="google-manual-token-input"
                  />
                  <Button
                    size="sm"
                    data-testid="google-manual-token-button"
                    disabled={savingToken || !google?.oauthConfigured}
                    onClick={connectWithToken}
                  >
                    {savingToken ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
                    Connect
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  The token is verified with Google before saving. This path has no redirect, no PKCE, and no
                  callback — the errors you have been seeing cannot occur here.
                </p>
              </div>
              {google?.clientId && (
                <p className="text-[11px] text-muted-foreground font-mono" data-testid="google-active-client">
                  Active OAuth client: {google.clientId}
                </p>
              )}
            </div>
          )}
          {google?.codeVersion && (
            <p className="text-[11px] text-muted-foreground font-mono" data-testid="google-code-version">
              Server OAuth build: {google.codeVersion}
            </p>
          )}

          <Accordion type="single" collapsible className="rounded-md border border-border px-3">
            <AccordionItem value="how" className="border-b border-border">
              <AccordionTrigger className="text-sm py-3" data-testid="sheets-walkthrough-trigger">
                What syncs automatically once connected?
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground space-y-2.5 pb-4">
                <p><strong className="text-foreground">Folders:</strong> a "GetYourQSIP Portal" folder in your Drive, with one "Member — email" subfolder per user (created the moment they register).</p>
                <p><strong className="text-foreground">Documents:</strong> every submitted matter document uploads into the member's folder; finished reports upload there too when you attach them.</p>
                <p><strong className="text-foreground">Receipts:</strong> a PDF receipt is generated and filed for every report and membership payment.</p>
                <p><strong className="text-foreground">Sheets:</strong> a Master Orders sheet plus a per-member orders sheet get a new row for every event — created, verified, mismatched, paid, refunded, report delivered.</p>
                <p><strong className="text-foreground">Emails:</strong> your admin notification address receives a confirmation email for each sync so you can verify it is working.</p>
                <p><strong className="text-foreground">After deployment:</strong> the connection keeps working. Only add your deployed domain's redirect URI in Google Cloud → Credentials if you ever reconnect from the live site.</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="legacy" className="border-0">
              <AccordionTrigger className="text-sm py-3" data-testid="sheets-legacy-trigger">
                Advanced: service-account fallback (rows only, optional)
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pb-4">
                <p className="text-xs text-muted-foreground">
                  Only needed if you prefer exporting rows to a sheet YOU create manually and share with a
                  service account. File uploads are not possible this way (Google policy).
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="sheet-id">Google Sheet ID</Label>
                    <Input
                      id="sheet-id"
                      data-testid="settings-sheet-id-input"
                      className="font-mono text-xs"
                      placeholder="1AbC…xYz (from the sheet URL)"
                      value={form.googleSheetId}
                      onChange={setField("googleSheetId")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="sheet-tab">Tab name</Label>
                    <Input
                      id="sheet-tab"
                      data-testid="settings-sheet-tab-input"
                      placeholder="Orders"
                      value={form.googleSheetTab}
                      onChange={setField("googleSheetTab")}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sa-json">Service account JSON key</Label>
                  <Textarea
                    id="sa-json"
                    data-testid="settings-sa-json-input"
                    className="min-h-[96px] font-mono text-xs"
                    placeholder={
                      settings.hasServiceAccountJson
                        ? `Configured (${settings.serviceAccountEmail}). Paste a new key here only to replace it.`
                        : '{ "type": "service_account", "client_email": "…", "private_key": "…" }'
                    }
                    value={form.googleServiceAccountJson}
                    onChange={setField("googleServiceAccountJson")}
                  />
                  {settings.hasServiceAccountJson && (
                    <p className="text-xs text-muted-foreground" data-testid="settings-sa-email">
                      Share your sheet (Editor) with: <span className="font-mono">{settings.serviceAccountEmail}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    data-testid="settings-save-sheets-button"
                    disabled={saving}
                    onClick={() => save(["googleSheetId", "googleSheetTab", "googleServiceAccountJson"])}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="settings-test-sheets-button"
                    disabled={testingSheets}
                    onClick={testSheets}
                  >
                    {testingSheets ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FlaskConical className="h-4 w-4 mr-1.5" />}
                    Test connection
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
      </div>

      <div className="space-y-6">
        {/* Email notifications */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Mail className="h-4.5 w-4.5 h-5 w-5 text-primary" /> Email notifications (Resend)
              </CardTitle>
              <ConfiguredPill
                ok={settings.resendConfigured}
                okLabel="API key active"
                badLabel="No API key"
                testid="resend-configured-pill"
              />
            </div>
            <CardDescription>
              Order and ticket alerts are emailed to the admin address; members get receipts and replies.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="admin-email">Admin notification email</Label>
                <Input
                  id="admin-email"
                  data-testid="settings-admin-email-input"
                  type="email"
                  placeholder="ops@yourdomain.com"
                  value={form.adminNotifyEmail}
                  onChange={setField("adminNotifyEmail")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sender-email">Sender address</Label>
                <Input
                  id="sender-email"
                  data-testid="settings-sender-email-input"
                  type="email"
                  placeholder="onboarding@resend.dev"
                  value={form.senderEmail}
                  onChange={setField("senderEmail")}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Emails send from your verified domain address (default: GetYourQSIP
              &lt;admin@getyourqsip.com&gt;). You can use any address at a domain verified in your Resend
              account.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                data-testid="settings-save-email-button"
                disabled={saving}
                onClick={() => save(["adminNotifyEmail", "senderEmail"])}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                data-testid="settings-send-digest-button"
                disabled={sendingDigest}
                onClick={sendDigest}
              >
                {sendingDigest ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <CalendarClock className="h-4 w-4 mr-1.5" />
                )}
                Send weekly digest now
              </Button>
              <Button
                size="sm"
                variant="outline"
                data-testid="settings-send-reminders-button"
                disabled={sendingReminders}
                onClick={sendReminders}
              >
                {sendingReminders ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <BellRing className="h-4 w-4 mr-1.5" />
                )}
                Send order reminders now
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              A digest with new orders, pending verifications, and open tickets is emailed automatically every
              Monday to the admin address above. Members with orders sitting unpaid or unconfirmed for 7+ days
              automatically receive a reminder email, repeated every 7 days until they act.
            </p>
          </CardContent>
        </Card>

        {/* Telegram */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Send className="h-4.5 w-4.5 h-5 w-5 text-primary" /> Telegram alerts
              </CardTitle>
              <ConfiguredPill
                ok={Boolean(settings.telegramBotTokenMasked && settings.telegramChatId)}
                okLabel="Configured"
                badLabel="Not configured"
                testid="telegram-configured-pill"
              />
            </div>
            <CardDescription>
              Message @BotFather in Telegram, send /newbot, and copy the token. Then message your bot once and use
              @userinfobot to get your chat ID.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tg-token">Bot token</Label>
                <Input
                  id="tg-token"
                  data-testid="settings-telegram-token-input"
                  type="password"
                  className="font-mono text-xs"
                  placeholder={settings.telegramBotTokenMasked || "123456:ABC-DEF…"}
                  value={form.telegramBotToken}
                  onChange={setField("telegramBotToken")}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tg-chat">Chat ID</Label>
                <Input
                  id="tg-chat"
                  data-testid="settings-telegram-chat-input"
                  className="font-mono text-xs"
                  placeholder="e.g. 123456789"
                  value={form.telegramChatId}
                  onChange={setField("telegramChatId")}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                data-testid="settings-save-telegram-button"
                disabled={saving}
                onClick={() => save(["telegramBotToken", "telegramChatId"])}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                data-testid="settings-test-telegram-button"
                disabled={testingTelegram}
                onClick={testTelegram}
              >
                {testingTelegram ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FlaskConical className="h-4 w-4 mr-1.5" />}
                Send test message
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Beta invite codes */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" /> Beta invite codes
            </CardTitle>
            <CardDescription>
              Single-use registration codes granting a free 2-week Enterprise trial. Report requests remain
              paid during the trial. Share a code, or send the direct link shown when you copy one.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {inviteCodes === null ? (
              <Skeleton className="h-24 w-full" />
            ) : inviteCodes.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="invite-codes-empty">
                No codes yet — generate a batch below.
              </p>
            ) : (
              <div className="space-y-1.5" data-testid="invite-codes-list">
                {inviteCodes.map((invite) => {
                  const exhausted = invite.used_count >= invite.max_uses;
                  return (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-border bg-secondary/30 px-3 py-2"
                      data-testid={`invite-code-row-${invite.code}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`font-mono text-sm ${exhausted ? "line-through text-muted-foreground" : "text-foreground"}`}>
                          {invite.code}
                        </span>
                        <span
                          className={`text-[11px] rounded border px-1.5 py-0.5 ${
                            exhausted
                              ? "border-border text-muted-foreground"
                              : "border-[hsl(var(--status-verified))]/40 text-[hsl(var(--status-verified))]"
                          }`}
                        >
                          {exhausted ? `used${invite.used_by?.length ? ` by ${invite.used_by[0]}` : ""}` : "available"}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={exhausted}
                        onClick={() => copyCode(invite.code)}
                        data-testid={`invite-code-copy-${invite.code}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            <Button
              size="sm"
              variant="secondary"
              disabled={generatingCodes}
              onClick={generateCodes}
              data-testid="generate-invite-codes-button"
            >
              {generatingCodes ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />}
              Generate 5 more codes
            </Button>
            <p className="text-[11px] text-muted-foreground">
              Members redeem codes on the Register tab, or via direct link:{" "}
              <span className="font-mono">/auth?mode=register&amp;invite=CODE</span>
            </p>
          </CardContent>
        </Card>

        {/* Maintenance / reset test data */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-primary" /> Test member
            </CardTitle>
            <CardDescription>
              Creates (or resets) a ready-to-use premium account so you can verify member flows on this
              environment: <span className="font-mono text-foreground">test-premium@getyourqsip.com</span> /{" "}
              <span className="font-mono text-foreground">Premium@2025!</span> with an active Pro membership.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              size="sm"
              variant="secondary"
              disabled={creatingTestMember}
              onClick={createTestMember}
              data-testid="create-test-member-button"
            >
              {creatingTestMember ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FlaskConical className="h-4 w-4 mr-1.5" />}
              Create / reset test member
            </Button>
            <p className="text-[11px] text-muted-foreground">
              Live Stripe is configured — do not complete real report payments while testing. Remove the test
              account later with Clear test data (member accounts) below.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" /> Clear test data
            </CardTitle>
            <CardDescription>
              Remove the test tickets and cases created during setup so real members start with a clean
              portal. Google, Stripe, Resend, and Telegram connections are never touched.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2.5">
              <label className="flex items-center gap-2.5 text-sm text-foreground cursor-pointer">
                <Checkbox
                  checked={resetOptions.tickets}
                  onCheckedChange={(checked) => setResetOptions((prev) => ({ ...prev, tickets: Boolean(checked) }))}
                  data-testid="reset-tickets-checkbox"
                />
                Support tickets and their messages
              </label>
              <label className="flex items-center gap-2.5 text-sm text-foreground cursor-pointer">
                <Checkbox
                  checked={resetOptions.orders}
                  onCheckedChange={(checked) => setResetOptions((prev) => ({ ...prev, orders: Boolean(checked) }))}
                  data-testid="reset-orders-checkbox"
                />
                Cases / orders (uploaded documents, extractions, report batches)
              </label>
              <label className="flex items-center gap-2.5 text-sm text-foreground cursor-pointer">
                <Checkbox
                  checked={resetOptions.members}
                  onCheckedChange={(checked) => setResetOptions((prev) => ({ ...prev, members: Boolean(checked) }))}
                  data-testid="reset-members-checkbox"
                />
                Test member accounts (keeps the admin account)
              </label>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="destructive"
                  data-testid="reset-test-data-button"
                  disabled={resetting || (!resetOptions.tickets && !resetOptions.orders && !resetOptions.members)}
                >
                  {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
                  Clear selected test data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Permanently delete the selected test data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This cannot be undone. Integrations, settings, and the admin account stay intact
                    {resetOptions.orders ? ", and uploaded test files are removed from storage" : ""}.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="reset-cancel-button">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="reset-confirm-button"
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={runReset}
                  >
                    Yes, clear it
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
